import { isPreReady } from 'functions/src/types/firestore/Game/Tournament/util';
import { useMemo } from 'react';
import { CompetitionBody } from 'src/components/leaderboards/CompetitionAcccordion';
import { useCurrency } from 'src/contexts/CurrencyContext';
import { useServerTime } from 'src/contexts/useServerTime';
import { useDateFormatter } from './useDateFormatter';

export type UseAccordionizeCompetitionsParams = {
  competitions: CompetitionBody[];
  chosenTournamentId?: string;
};

export const useAccordionizeCompetitions = ({
  competitions,
  chosenTournamentId,
}: UseAccordionizeCompetitionsParams) => {
  const { format } = useCurrency();
  const { now } = useServerTime();
  const dateFormat = useDateFormatter();

  const competitionsSorted = useMemo(() => {
    return competitions.sort((a, b) => {
      if (isPreReady(a.phase!) && isPreReady(b.phase!)) {
        return (
          (a.date ? a.date.getTime() : 0) - (b.date ? b.date.getTime() : 0)
        );
      } else {
        return (
          (b.date ? b.date.getTime() : 0) - (a.date ? a.date.getTime() : 0)
        );
      }
    });
  }, [competitions]);
  const chosen = useMemo(() => {
    return chosenTournamentId && competitionsSorted?.length > 0
      ? Math.max(
          competitionsSorted.findIndex((competition) => {
            return competition.id === chosenTournamentId;
          }),
          0,
        )
      : undefined;
  }, [chosenTournamentId, competitionsSorted]);
  const competitionsInternal = useMemo(() => {
    return competitionsSorted.map(
      ({ prize, title, date, endDate, Details, phase }) => {
        const prizeFormatted = format(prize.amount, prize.currency, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
        const titleFormatted = title.replace(prizeFormatted, '').trim();
        const dateFormatted = dateFormat(date);
        const summary = `${prizeFormatted} ${titleFormatted} ${dateFormatted}`;
        const isLive = !!phase
          ? phase === 'live'
          : date.getTime() < now() && now() < endDate.getTime();
        return {
          summary,
          isLive,
          Details,
        };
      },
    );
  }, [competitionsSorted, dateFormat, format, now]);
  return { chosen, competitionsInternal };
};
